// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import {getDaoIThemeSnapshot} from '@app/features/daoi/bridge/DaoITheme';
import {DaoIWallet} from '@app/features/daoi/bridge/DaoIWallet';
import {http} from '@app/features/platform/transport/RestTransport';
import {Button} from '@app/features/ui/button/Button';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const BUSINESS_ORIGIN = 'https://daoi.ai';
const CHANNEL = 'daoi-embed';
type BusinessView = 'mint' | 'swap' | 'partner';

interface HostProps {
	view: BusinessView;
	active: boolean;
	theme: 'dark' | 'light';
	walletAddress: string | null;
	onNavigate: (view: BusinessView) => void;
}

export const DaoIBusinessHost = observer((props: HostProps) => {
	const iframe = useRef<HTMLIFrameElement>(null);
	const current = useRef(props);
	current.current = props;
	const [ready, setReady] = useState(false);
	const [failed, setFailed] = useState(false);
	const [loadKey, setLoadKey] = useState(0);
	const seenIds = useRef(new Set<string>());
	const pendingIds = useRef(new Set<string>());
	const send = (type: string, payload: unknown, id?: string) => {
		iframe.current?.contentWindow?.postMessage(
			{channel: CHANNEL, version: 1, type, payload, ...(id ? {id} : {})},
			BUSINESS_ORIGIN,
		);
	};
	const state = () => ({
		walletAddress: current.current.walletAddress,
		chainId: DaoIWallet.chainId,
		theme: current.current.theme,
		appearance: getDaoIThemeSnapshot(),
		view: current.current.view,
		active: current.current.active,
	});

	useEffect(() => {
		if (ready) send('state', state());
	}, [
		ready,
		props.active,
		props.view,
		props.theme,
		props.walletAddress,
		DaoIWallet.chainId,
		DaoIWallet.account,
		Accessibility.useReducedMotion,
	]);

	useEffect(() => {
		if (!ready) return;
		let frame = 0;
		const syncAppearance = () => {
			cancelAnimationFrame(frame);
			frame = requestAnimationFrame(() => send('state', state()));
		};
		const observer = new MutationObserver(syncAppearance);
		observer.observe(document.documentElement, {attributes: true, attributeFilter: ['class', 'style']});
		const themeStyleId = 'fluxer-custom-theme-style';
		const customThemeObserver = new MutationObserver((mutations) => {
			if (
				mutations.some(
					(mutation) =>
						(mutation.target instanceof Element && mutation.target.closest(`#${themeStyleId}`)) ||
						mutation.target.parentElement?.closest(`#${themeStyleId}`) ||
						[...mutation.addedNodes, ...mutation.removedNodes].some(
							(node) => node instanceof Element && node.id === themeStyleId,
						),
				)
			)
				syncAppearance();
		});
		customThemeObserver.observe(document.head, {childList: true, characterData: true, subtree: true});
		const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
		motion.addEventListener('change', syncAppearance);
		window.addEventListener('resize', syncAppearance);
		return () => {
			observer.disconnect();
			customThemeObserver.disconnect();
			cancelAnimationFrame(frame);
			motion.removeEventListener('change', syncAppearance);
			window.removeEventListener('resize', syncAppearance);
		};
	}, [ready]);

	useEffect(() => {
		const unsubscribe = DaoIWallet.subscribe((event, args) => send('wallet:event', {event, args}));
		const receive = async (event: MessageEvent) => {
			if (event.origin !== BUSINESS_ORIGIN || event.source !== iframe.current?.contentWindow) return;
			const message = event.data;
			if (!message || typeof message !== 'object' || message.channel !== CHANNEL || message.version !== 1) return;
			if (message.type === 'ready') {
				setReady(true);
				setFailed(false);
				send('init', state());
				return;
			}
			if (message.type === 'navigate') {
				if (current.current.active && ['mint', 'swap', 'partner'].includes(message.payload?.view))
					current.current.onNavigate(message.payload.view);
				return;
			}
			const id = message.id;
			if (
				typeof id !== 'string' ||
				id.length < 1 ||
				id.length > 128 ||
				seenIds.current.has(id) ||
				pendingIds.current.has(id)
			)
				return;
			if (
				![
					'wallet:request',
					'quote:register',
					'transaction:submitted',
					'history:request',
					'tokens:request',
					'tokens:updated',
				].includes(message.type)
			)
				return;
			if (pendingIds.current.size >= 64) {
				send('bridge:error', {error: {code: 429, message: '请求较多，请稍后重试。'}}, id);
				return;
			}
			pendingIds.current.add(id);
			try {
				if (message.type === 'wallet:request') {
					const method = message.payload?.method;
					if (typeof method !== 'string') throw new Error('钱包请求格式错误。');
					const result = await DaoIWallet.request(method, message.payload?.params, current.current.active);
					send('wallet:response', {result}, id);
				} else if (message.type === 'quote:register') {
					if (!current.current.active || !DaoIWallet.matchesBound) throw new Error('请连接绑定钱包并返回 Swap 页面。');
					if (typeof message.payload?.quoteProof !== 'string') throw new Error('报价凭据无效。');
					const response = await http.post<{intent_id: string}>('/daoi/swap-intents', {
						body: {quote_proof: message.payload.quoteProof},
					});
					send('quote:registered', {intentId: response.body.intent_id}, id);
				} else if (message.type === 'history:request') {
					const query = new URLSearchParams({limit: '30'});
					if (typeof message.payload?.before === 'string') query.set('before', message.payload.before);
					const response = await http.get(`/daoi/swap-transactions?${query}`);
					send('history:response', response.body, id);
				} else if (message.type === 'tokens:request') {
					const response = await http.get('/daoi/wallet-tokens');
					send('tokens:response', response.body, id);
				} else if (message.type === 'tokens:updated') {
					if (!current.current.active) throw new Error('请返回业务页面后更新代币。');
					const response = await http.post('/daoi/wallet-tokens', {body: {tokens: message.payload?.tokens}});
					send('tokens:stored', response.body, id);
				} else {
					// Recording an already submitted transaction is allowed after switching tabs.
					const response = await http.post('/daoi/swap-transactions', {
						body: {
							intent_id: message.payload?.intentId,
							chain_id: message.payload?.chainId,
							tx_hash: message.payload?.txHash,
						},
					});
					send('transaction:recorded', response.body, id);
				}
			} catch (error) {
				const details = error as {code?: number; message?: string};
				const payload = {
					error: {
						code: typeof details.code === 'number' ? details.code : 4001,
						message: details.message || '操作失败，请重试。',
					},
				};
				send(message.type === 'wallet:request' ? 'wallet:response' : 'bridge:error', payload, id);
			} finally {
				pendingIds.current.delete(id);
				seenIds.current.add(id);
				if (seenIds.current.size > 512) {
					const oldest = seenIds.current.values().next().value;
					if (oldest) seenIds.current.delete(oldest);
				}
			}
		};
		window.addEventListener('message', receive);
		return () => {
			unsubscribe();
			window.removeEventListener('message', receive);
		};
	}, []);

	useEffect(() => {
		if (ready) return;
		const timer = window.setTimeout(() => setFailed(true), 20000);
		return () => window.clearTimeout(timer);
	}, [ready, loadKey]);

	return (
		<section
			className="daoi-business-host"
			hidden={!props.active}
			aria-label={props.view === 'mint' ? 'DAOI 铸造' : props.view === 'swap' ? 'Swap' : '合伙人计划'}
			data-flx="daoi.bridge.dao-i-business-host.daoi-business-host"
		>
			{!ready && (
				<div className="daoi-state" role="status" data-flx="daoi.bridge.dao-i-business-host.daoi-state">
					{failed ? (
						<>
							<p data-flx="daoi.bridge.dao-i-business-host.p">业务页面暂时无法加载，聊天仍可正常使用。</p>
							<Button
								onClick={() => {
									seenIds.current.clear();
									setFailed(false);
									setReady(false);
									setLoadKey((key) => key + 1);
								}}
								data-flx="daoi.bridge.dao-i-business-host.daoi-button.clear"
							>
								重新加载
							</Button>
						</>
					) : (
						<p data-flx="daoi.bridge.dao-i-business-host.p--2">正在加载业务页面…</p>
					)}
				</div>
			)}
			<iframe
				key={loadKey}
				ref={iframe}
				src={`${BUSINESS_ORIGIN}/chat-embed`}
				title="DAOI 铸造与 Swap"
				className="daoi-business-frame"
				allow="clipboard-write"
				referrerPolicy="strict-origin-when-cross-origin"
				onLoad={() => {
					seenIds.current.clear();
					send('init', state());
				}}
				onError={() => setFailed(true)}
				data-flx="daoi.bridge.dao-i-business-host.daoi-business-frame"
			/>
		</section>
	);
});
